import { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import LaunchIcon from '@mui/icons-material/Launch'
import PersonSearchIcon from '@mui/icons-material/PersonSearch'
import SupportHeader from 'src/supportPortal/layout/SupportHeader'
import {
  getCareOrchestrationCases,
  searchCareOrchestrationPatientMatchesByPhone,
} from 'src/supportPortal/services/careOrchestrationService'
import {
  getProspect as fetchProspectBundle,
  getReportDownloadUrl,
} from 'src/supportPortal/services/patientProspectsApi'
import CareOrchestrationPatientMatchDrawer from './CareOrchestrationPatientMatchDrawer'

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'INTAKE_IN_PROGRESS', label: 'Intake in progress' },
  { value: 'TRIAGED', label: 'Triaged' },
  { value: 'AWAITING_BOOKING', label: 'Awaiting booking' },
  { value: 'CALLBACK_REQUESTED', label: 'Callback requested' },
  { value: 'ESCALATED_URGENT', label: 'Escalated urgent' },
]

const CHANNEL_OPTIONS = [
  { value: '', label: 'All channels' },
  { value: 'WEB', label: 'Portal' },
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'CALL', label: 'Call' },
]

function cleanPhone(rawValue) {
  return `${rawValue || ''}`.replace(/[^\d+]/g, '').trim()
}

function buildWhatsappLeadUrl(phone) {
  const digits = phone?.replace(/[^\d]/g, '')
  return digits ? `https://wa.me/${digits}` : null
}

function phoneMatchKey(phone) {
  const digits = `${phone || ''}`.replace(/\D/g, '')
  if (!digits) return ''
  return digits.length > 10 ? digits.slice(-10) : digits
}

function isFilled(value) {
  if (value == null || value === '') return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

function firstFilled(...values) {
  return values.find(isFilled)
}

function humanizeKey(key) {
  return `${key || ''}`
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^./, (char) => char.toUpperCase())
}

function formatDateTime(value) {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  return date.toLocaleString()
}

function formatBytes(bytes) {
  if (!bytes || !Number.isFinite(Number(bytes))) return ''
  const size = Number(bytes)
  if (size >= 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`
  return `${Math.max(1, Math.round(size / 1024))} KB`
}

function getReportId(report, fallback) {
  return report?.id || report?.reportId || report?.mediaId || report?.fileId || fallback
}

function getProspect(item) {
  return (
    item?.prospect ||
    item?.patientProspect ||
    item?.patientProspectInfo ||
    item?.prospectInfo ||
    item?.prospectInfoSnapshot ||
    {}
  )
}

function getIntakeAnswers(item) {
  const prospect = getProspect(item)
  return {
    ...(item?.triageAssessment?.answers || {}),
    ...(item?.careCase?.intakeAnswers || {}),
    ...(item?.intakeAnswers || {}),
    ...(prospect?.intakeAnswers || {}),
  }
}

function getReports(item) {
  const prospect = getProspect(item)
  const candidates = [
    prospect?.reports,
    item?.reports,
    item?.uploadedReports,
    item?.uploads,
    item?.documents,
    item?.careCase?.reports,
  ]
  return candidates.find((candidate) => Array.isArray(candidate) && candidate.length > 0) || []
}

function hasPatientMatchPayload(item) {
  return Array.isArray(item?.matchedPatients)
}

function getBackendPatientMatches(item) {
  return hasPatientMatchPayload(item) ? item.matchedPatients : []
}

function getReportUserId(item) {
  const prospect = getProspect(item)
  return (
    prospect?.userId ||
    item?.userId ||
    item?.lead?.userId ||
    item?.lead?.linkedUserId ||
    item?.careCase?.userId ||
    item?.careCase?.createdByUserId ||
    null
  )
}

function hasProspectDetails(item) {
  const prospect = getProspect(item)
  return Boolean(
    isFilled(prospect?.intakeAnswers) ||
    isFilled(prospect?.reports) ||
    isFilled(prospect?.pathway) ||
    isFilled(prospect?.specialtyTag) ||
    isFilled(prospect?.preferredClinicHint),
  )
}

async function enrichCaseWithProspectBundle(item) {
  if (hasProspectDetails(item)) return item
  const userId = getReportUserId(item)
  if (!userId) return item

  try {
    const response = await fetchProspectBundle(userId)
    if (!(response?.status >= 200 && response?.status < 300)) return item
    const bundle = response.data || {}
    return {
      ...item,
      prospect: bundle.prospect || item?.prospect,
      careCase: {
        ...(bundle.careCase || {}),
        ...(item?.careCase || {}),
      },
      recommendation: item?.recommendation || bundle.recommendation,
    }
  } catch (error) {
    console.error('Error enriching care orchestration case with prospect bundle:', error)
    return item
  }
}

const PATHWAY_LABELS = {
  PSYCHIATRY: 'Psychiatry',
  THERAPY: 'Therapy / Psychology',
  BIOLOGICAL: 'Biological care',
  CHILD: 'Child growth & care',
  ASSESSMENT: 'Assessment / Help deciding',
  COMBINED: 'Combined care',
}

const SPECIALTY_LABELS = {
  psychiatry: 'Psychiatry',
  psychology: 'Therapy / Psychology',
  endocrinology: 'Endocrinology',
  rheumatology: 'Rheumatology',
  pediatrics: 'Pediatrics',
  triage: 'Triage',
  'mental-health-triage': 'Mental health triage',
}

const CONCERN_LABELS = {
  anxiety: 'Anxiety',
  low_mood: 'Low mood',
  sleep: 'Sleep',
  focus: 'Focus / attention',
  addiction: 'Addiction / substance use',
  relationships: 'Relationships',
  work_stress: 'Work / stress',
  grief: 'Grief / loss',
  thyroid: 'Thyroid',
  diabetes: 'Diabetes',
  pcos: 'PCOS / period concerns',
  weight: 'Weight',
  autoimmune: 'Autoimmune flare',
  joint_skin: 'Joint / skin',
  unexplained: 'Unexplained inflammation',
  growth: 'Growth',
  behavior: 'Behaviour',
  learning: 'Learning',
  nutrition: 'Nutrition',
  not_sure: "I'm not sure yet",
  general: 'General check-in',
  other: 'Something else',
}

const DURATION_LABELS = {
  DAYS: 'A few days',
  WEEKS: 'A few weeks',
  MONTHS: 'Months',
  LONG_TERM: 'Long term - over a year',
}

const PRIOR_SUPPORT_LABELS = {
  NONE: 'Nothing yet',
  THERAPY: 'Therapy',
  MEDICATION: 'Medication',
  BOTH: 'Both therapy and medication',
}

const SUBJECT_TYPE_LABELS = {
  SELF: 'For me',
  CHILD: 'For my child',
  DEPENDENT: 'For someone I care for',
}

function prettifyValue(value, key) {
  if (!isFilled(value)) return ''
  if (typeof value === 'boolean') return value ? 'Yes' : 'No'
  if (key === 'duration') return DURATION_LABELS[value] || value
  if (key === 'priorSupportHistory') return PRIOR_SUPPORT_LABELS[value] || value
  if (key === 'subjectType') return SUBJECT_TYPE_LABELS[value] || value
  if (key === 'pathway' || key === 'provisionalPathway' || key === 'primaryRecommendation') {
    return PATHWAY_LABELS[value] || value
  }
  if (key === 'specialtyTag') return SPECIALTY_LABELS[value] || value
  if (Array.isArray(value)) {
    return value.map((entry) => CONCERN_LABELS[entry] || SPECIALTY_LABELS[entry] || entry)
  }
  return value
}

function DetailBlock({ title, rows }) {
  const visibleRows = rows.filter((row) => isFilled(row.value))
  if (visibleRows.length === 0) return null
  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        {title}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
          gap: 1.25,
        }}
      >
        {visibleRows.map((row) => {
          const value = prettifyValue(row.value, row.key)
          return (
            <Paper key={`${title}-${row.label}`} variant="outlined" sx={{ p: 1.25, borderRadius: 2, backgroundColor: '#fbfdff' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: 11 }}>
                {row.label}
              </Typography>
              {Array.isArray(value) ? (
                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 0.5 }}>
                  {value.map((entry, index) => (
                    <Chip key={`${entry}-${index}`} size="small" label={String(entry)} />
                  ))}
                </Stack>
              ) : typeof value === 'object' ? (
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', mt: 0.25 }}>
                  {JSON.stringify(value, null, 2)}
                </Typography>
              ) : (
                <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', mt: 0.25 }}>
                  {String(value)}
                </Typography>
              )}
            </Paper>
          )
        })}
      </Box>
    </Box>
  )
}

const KNOWN_INTAKE_KEYS = new Set([
  'presentingConcerns',
  'freeText',
  'mainConcern',
  'duration',
  'priorSupportHistory',
  'previousCare',
  'medicalHistorySummary',
  'preferredClinicHint',
  'city',
  'preferredLanguage',
  'subject',
  'subjectType',
  'bookingIntent',
])

function SubmittedDetails({ item }) {
  const careCase = item?.careCase || {}
  const lead = item?.lead || {}
  const prospect = getProspect(item)
  const intake = getIntakeAnswers(item)
  const subject = intake.subject || {}
  const rows = [
    {
      label: 'Presenting concerns',
      key: 'presentingConcerns',
      value: firstFilled(intake.presentingConcerns, careCase.presentingConcerns),
    },
    {
      label: 'In their own words',
      key: 'freeText',
      value: firstFilled(intake.freeText, intake.mainConcern, careCase.presentingConcernText),
    },
    { label: 'Duration', key: 'duration', value: firstFilled(intake.duration, careCase.duration) },
    {
      label: 'Prior support',
      key: 'priorSupportHistory',
      value: firstFilled(intake.priorSupportHistory, intake.previousCare, careCase.priorSupportHistory),
    },
    { label: 'Medical history', key: 'medicalHistorySummary', value: intake.medicalHistorySummary },
    {
      label: 'Preferred clinic hint',
      key: 'preferredClinicHint',
      value: firstFilled(prospect.preferredClinicHint, intake.preferredClinicHint),
    },
    { label: 'City', key: 'city', value: firstFilled(intake.city, careCase.city, lead.city) },
    {
      label: 'Preferred language',
      key: 'preferredLanguage',
      value: firstFilled(intake.preferredLanguage, careCase.preferredLanguage, lead.preferredLanguage),
    },
    {
      label: 'Subject type',
      key: 'subjectType',
      value: firstFilled(prospect.subjectType, careCase.subjectType, intake.subjectType),
    },
    { label: 'Subject name', key: 'subjectName', value: subject.name },
    { label: 'Subject DOB', key: 'subjectDob', value: subject.dob },
    { label: 'Subject gender', key: 'subjectGender', value: subject.gender },
    { label: 'Booking intent', key: 'bookingIntent', value: firstFilled(intake.bookingIntent, prospect.bookingIntent, item?.bookingIntent) },
    {
      label: 'Submitted to care team',
      key: 'intakeSubmittedAt',
      value: prospect.intakeSubmittedAt ? formatDateTime(prospect.intakeSubmittedAt) : null,
    },
  ]

  const additionalRows = Object.entries(intake)
    .filter(([key, value]) => !KNOWN_INTAKE_KEYS.has(key) && isFilled(value))
    .map(([key, value]) => ({
      label: humanizeKey(key),
      key,
      value,
    }))

  return (
    <>
      <DetailBlock title="Submitted details" rows={[...rows, ...additionalRows]} />
      {[...rows, ...additionalRows].some((row) => isFilled(row.value)) ? null : (
        <Typography variant="body2" color="text.secondary">
          No detailed intake answers have been added yet.
        </Typography>
      )}
    </>
  )
}

function ReportsList({ item, openingReportId, onOpenReport }) {
  const reports = getReports(item)
  const userId = getReportUserId(item)

  if (reports.length === 0) {
    return (
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
          Uploads
        </Typography>
        <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2, backgroundColor: '#fbfdff' }}>
          <Typography variant="body2" color="text.secondary">
            No reports or documents uploaded.
          </Typography>
        </Paper>
      </Box>
    )
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Uploads ({reports.length})
      </Typography>
      <Stack spacing={1}>
        {reports.map((report, index) => {
          const reportId = getReportId(report, index)
          const directUrl = report?.url || report?.downloadUrl || report?.signedUrl
          const canOpen = Boolean(directUrl || (userId && (report?.id || report?.reportId) && (report?.mediaId || report?.fileId)))
          return (
            <Paper
              key={reportId}
              variant="outlined"
              sx={{ p: 1.25, borderRadius: 2, display: 'grid', gridTemplateColumns: '1fr auto', gap: 1, alignItems: 'center' }}
            >
              <Box sx={{ minWidth: 0 }}>
                <Stack direction="row" spacing={0.75} alignItems="center">
                  <AttachFileIcon sx={{ fontSize: 17, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-word' }}>
                    {report?.fileName || report?.name || report?.label || 'Uploaded file'}
                  </Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {[report?.contentType || report?.mimeType, formatBytes(report?.sizeBytes || report?.size), report?.uploadedAt ? formatDateTime(report.uploadedAt) : null]
                    .filter(Boolean)
                    .join(' - ')}
                </Typography>
              </Box>
              {canOpen ? (
                <Button
                  size="small"
                  startIcon={<LaunchIcon fontSize="small" />}
                  disabled={openingReportId === reportId}
                  onClick={() => onOpenReport(report, userId)}
                >
                  {openingReportId === reportId ? 'Opening...' : 'Open'}
                </Button>
              ) : null}
            </Paper>
          )
        })}
      </Stack>
    </Box>
  )
}

function ResultCard({
  item,
  patientMatches = [],
  patientMatchesLoading = false,
  onOpenPatientMatches,
  openingReportId,
  onOpenReport,
}) {
  const careCase = item?.careCase || {}
  const lead = item?.lead || {}
  const prospect = getProspect(item)
  const intake = getIntakeAnswers(item)
  const recommendation = item?.recommendation || {}
  const triageAssessment = item?.triageAssessment || {}
  const referralTask = item?.referralTask || {}
  const whatsappLeadUrl = buildWhatsappLeadUrl(lead.phone)
  const chosenPathway = firstFilled(prospect.pathway, careCase.provisionalPathway, recommendation.primaryRecommendation)
  const specialtyTag = firstFilled(prospect.specialtyTag, careCase.specialtyTag, item?.specialtyTag)
  const trackRows = [
    { label: 'Chosen track', key: 'pathway', value: chosenPathway },
    { label: 'Specialty', key: 'specialtyTag', value: specialtyTag },
    { label: 'Subject type', key: 'subjectType', value: firstFilled(prospect.subjectType, careCase.subjectType, intake.subjectType) },
    { label: 'Source', key: 'source', value: firstFilled(prospect.source, lead.source, careCase.channel) },
    { label: 'Prospect status', key: 'status', value: prospect.status },
    { label: 'Intake submitted', key: 'intakeSubmittedAt', value: prospect.intakeSubmittedAt ? formatDateTime(prospect.intakeSubmittedAt) : null },
  ]

  return (
    <Card variant="outlined" sx={{ borderRadius: 2 }}>
      <CardContent>
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mb: 1.5 }}>
          <Chip label={careCase.channel || 'UNKNOWN'} size="small" color="primary" variant="outlined" />
          <Chip label={careCase.status || 'UNKNOWN'} size="small" color="secondary" variant="outlined" />
          {careCase.provisionalPathway ? (
            <Chip label={careCase.provisionalPathway} size="small" variant="outlined" />
          ) : null}
        </Stack>

        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          {lead.name || 'Unnamed lead'}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {careCase.id}
        </Typography>

        <Stack spacing={1}>
          <Typography variant="body2">
            <strong>Phone:</strong> {lead.phone || 'Not captured'}
          </Typography>
          {lead.email ? (
            <Typography variant="body2">
              <strong>Email:</strong> {lead.email}
            </Typography>
          ) : null}
          <Typography variant="body2">
            <strong>City:</strong> {careCase.city || lead.city || 'Not captured'}
          </Typography>
          <Typography variant="body2">
            <strong>Language:</strong> {careCase.preferredLanguage || lead.preferredLanguage || 'Not captured'}
          </Typography>
          <Typography variant="body2">
            <strong>Concern:</strong> {careCase.presentingConcernText || 'Not captured'}
          </Typography>
          <Typography variant="body2">
            <strong>Next step:</strong> {item?.nextActionLabel || careCase.recommendedNextStep || 'Pending'}
          </Typography>
          <Typography variant="body2">
            <strong>Urgency:</strong> {careCase.urgencyLevel || triageAssessment.severityBand || 'Pending'}
          </Typography>
          {careCase.preferredCallbackTime ? (
            <Typography variant="body2">
              <strong>Preferred callback time:</strong> {careCase.preferredCallbackTime}
            </Typography>
          ) : null}
          {recommendation.userFacingSummary ? (
            <Typography variant="body2">
              <strong>User summary:</strong> {recommendation.userFacingSummary}
            </Typography>
          ) : null}
          {referralTask.reason ? (
            <Typography variant="body2">
              <strong>Referral task:</strong> {referralTask.reason}
            </Typography>
          ) : null}
        </Stack>

        {lead.phone ? (
          <Box sx={{ mt: 2 }}>
            {patientMatchesLoading ? (
              <Alert severity="info" icon={<PersonSearchIcon />}>
                Checking for existing patients with this phone number...
              </Alert>
            ) : patientMatches.length > 0 ? (
              <Alert
                severity="warning"
                icon={<PersonSearchIcon />}
                action={
                  <Button size="small" color="inherit" onClick={onOpenPatientMatches}>
                    View patient & bookings
                  </Button>
                }
              >
                Phone matches {patientMatches.length} existing PatientInfo {patientMatches.length === 1 ? 'record' : 'records'}.
              </Alert>
            ) : (
              <Alert severity="info" icon={<PersonSearchIcon />}>
                No existing patient record matched this phone number.
              </Alert>
            )}
          </Box>
        ) : null}

        <Divider sx={{ my: 2 }} />

        <Stack spacing={2}>
          <DetailBlock title="Chosen track" rows={trackRows} />
          <SubmittedDetails item={item} />
          <ReportsList item={item} openingReportId={openingReportId} onOpenReport={onOpenReport} />
        </Stack>

        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ mt: 2 }}>
          {item?.bookingLink ? (
            <Button
              component={Link}
              href={item.bookingLink}
              target="_blank"
              rel="noreferrer"
              variant="contained"
              size="small"
            >
              Booking link
            </Button>
          ) : null}
          {item?.humanSupportWhatsappUrl ? (
            <Button
              component={Link}
              href={item.humanSupportWhatsappUrl}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              size="small"
            >
              Human support WhatsApp
            </Button>
          ) : null}
          {whatsappLeadUrl ? (
            <Button
              component={Link}
              href={whatsappLeadUrl}
              target="_blank"
              rel="noreferrer"
              variant="outlined"
              size="small"
            >
              Message lead
            </Button>
          ) : null}
        </Stack>
      </CardContent>
    </Card>
  )
}

export default function CareOrchestrationCasesPage() {
  const [draftFilters, setDraftFilters] = useState({
    status: '',
    channel: '',
    leadPhone: '',
  })
  const [filters, setFilters] = useState({
    status: '',
    channel: '',
    leadPhone: '',
  })
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [items, setItems] = useState([])
  const [patientMatchesByPhone, setPatientMatchesByPhone] = useState({})
  const [patientMatchesLoading, setPatientMatchesLoading] = useState(false)
  const [patientMatchDrawer, setPatientMatchDrawer] = useState({
    open: false,
    lead: null,
    patients: [],
  })
  const [openingReportId, setOpeningReportId] = useState(null)

  useEffect(() => {
    let cancelled = false

    async function load() {
      setLoading(true)
      setError('')
      try {
        const responseItems = await getCareOrchestrationCases(filters)
        const enrichedItems = await Promise.all(responseItems.map(enrichCaseWithProspectBundle))
        if (!cancelled) {
          setItems(enrichedItems)
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError?.message || 'Unable to load care orchestration cases.')
          setItems([])
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    load()
    return () => {
      cancelled = true
    }
  }, [filters])

  useEffect(() => {
    let cancelled = false
    const phones = Array.from(
      new Set(
        items
          .filter((item) => !hasPatientMatchPayload(item))
          .map((item) => item?.lead?.phone)
          .filter(Boolean)
          .map(cleanPhone)
          .filter(Boolean),
      ),
    )

    if (phones.length === 0) {
      setPatientMatchesByPhone({})
      setPatientMatchesLoading(false)
      return () => {
        cancelled = true
      }
    }

    setPatientMatchesLoading(true)
    Promise.all(
      phones.map(async (phone) => {
        try {
          const matches = await searchCareOrchestrationPatientMatchesByPhone(phone)
          return [phoneMatchKey(phone), matches]
        } catch (matchError) {
          console.error('Error searching patient matches:', matchError)
          return [phoneMatchKey(phone), []]
        }
      }),
    )
      .then((entries) => {
        if (cancelled) return
        setPatientMatchesByPhone(Object.fromEntries(entries))
      })
      .finally(() => {
        if (!cancelled) setPatientMatchesLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [items])

  async function openReport(report, userId) {
    const reportId = getReportId(report)
    const directUrl = report?.url || report?.downloadUrl || report?.signedUrl
    if (directUrl) {
      window.open(directUrl, '_blank', 'noreferrer')
      return
    }
    if (!userId || !(report?.id || report?.reportId)) return

    const openedWindow = window.open('', '_blank')
    setOpeningReportId(reportId)
    try {
      const response = await getReportDownloadUrl(userId, report.id || report.reportId)
      const url = response?.data?.url || response?.data?.data?.url
      if (url) {
        if (openedWindow) {
          openedWindow.location.href = url
        } else {
          window.open(url, '_blank', 'noreferrer')
        }
      } else if (openedWindow) {
        openedWindow.close()
      }
    } catch (openError) {
      if (openedWindow) openedWindow.close()
      console.error('Error opening report:', openError)
    } finally {
      setOpeningReportId(null)
    }
  }

  function applyFilters() {
    setFilters({
      status: draftFilters.status,
      channel: draftFilters.channel,
      leadPhone: cleanPhone(draftFilters.leadPhone),
    })
  }

  function clearFilters() {
    const cleared = {
      status: '',
      channel: '',
      leadPhone: '',
    }
    setDraftFilters(cleared)
    setFilters(cleared)
  }

  return (
    <>
      <SupportHeader heading="Care Orchestration Cases" showBackButton />
      <Box sx={{ p: { xs: 2, md: 3 }, display: 'grid', gap: 2.5 }}>
        <Card variant="outlined" sx={{ borderRadius: 3 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
              New guided intake workbench
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Review portal, WhatsApp, and callback-driven intake cases in one queue. This is the
              operational landing place for the new care orchestration front door.
            </Typography>

            <Stack
              direction={{ xs: 'column', md: 'row' }}
              spacing={2}
              useFlexGap
              sx={{ alignItems: { md: 'flex-end' } }}
            >
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={draftFilters.status}
                  label="Status"
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      status: event.target.value,
                    }))
                  }
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'all-statuses'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Channel</InputLabel>
                <Select
                  value={draftFilters.channel}
                  label="Channel"
                  onChange={(event) =>
                    setDraftFilters((current) => ({
                      ...current,
                      channel: event.target.value,
                    }))
                  }
                >
                  {CHANNEL_OPTIONS.map((option) => (
                    <MenuItem key={option.value || 'all-channels'} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                size="small"
                label="Lead phone"
                value={draftFilters.leadPhone}
                onChange={(event) =>
                  setDraftFilters((current) => ({
                    ...current,
                    leadPhone: event.target.value,
                  }))
                }
                placeholder="+91..."
              />

              <Stack direction="row" spacing={1} useFlexGap>
                <Button variant="contained" onClick={applyFilters}>
                  Apply
                </Button>
                <Button variant="outlined" onClick={clearFilters}>
                  Clear
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {error ? <Alert severity="error">{error}</Alert> : null}

        {loading ? (
          <Box sx={{ py: 8, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : items.length === 0 ? (
          <Alert severity="info">
            No care orchestration cases matched these filters yet.
          </Alert>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gap: 2,
              gridTemplateColumns: {
                xs: '1fr',
                xl: 'repeat(2, minmax(0, 1fr))',
              },
            }}
          >
            {items.map((item) => {
              const matchKey = phoneMatchKey(item?.lead?.phone)
              const hasBackendMatches = hasPatientMatchPayload(item)
              const patientMatches = hasBackendMatches
                ? getBackendPatientMatches(item)
                : patientMatchesByPhone[matchKey] || []
              return (
                <ResultCard
                  key={item?.careCase?.id || item?.lead?.id || JSON.stringify(item)}
                  item={item}
                  patientMatches={patientMatches}
                  patientMatchesLoading={hasBackendMatches ? false : patientMatchesLoading}
                  onOpenPatientMatches={() =>
                    setPatientMatchDrawer({
                      open: true,
                      lead: item?.lead || {},
                      patients: patientMatches,
                    })
                  }
                  openingReportId={openingReportId}
                  onOpenReport={openReport}
                />
              )
            })}
          </Box>
        )}
      </Box>
      <CareOrchestrationPatientMatchDrawer
        open={patientMatchDrawer.open}
        lead={patientMatchDrawer.lead}
        patients={patientMatchDrawer.patients}
        onClose={() =>
          setPatientMatchDrawer({
            open: false,
            lead: null,
            patients: [],
          })
        }
      />
    </>
  )
}
